import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Phone, Mail, Calendar, MapPin, Trash2, Users, Inbox, Briefcase, ChevronDown, ChevronRight, HeartHandshake } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  verifyAdmin,
  listBookings,
  listContacts,
  updateBookingStatus,
  deleteBooking,
  getStats,
  listDriverApplications,
  updateDriverApplicationStatus,
  deleteDriverApplication,
  listMonitorApplications,
  updateMonitorApplicationStatus,
  deleteMonitorApplication,
} from "@/lib/api";
import { COMPANY } from "@/lib/data";

const TOKEN_KEY = "cc_admin_token";

export default function AdminBookings() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);

  const verify = useCallback(async (t) => {
    setChecking(true);
    try {
      await verifyAdmin(t);
      localStorage.setItem(TOKEN_KEY, t);
      setAuthed(true);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (token) verify(token);
  }, [token, verify]);

  if (!authed) {
    return (
      <LoginCard
        token={token}
        setToken={setToken}
        onSubmit={() => verify(token)}
        loading={checking}
      />
    );
  }

  return <Dashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthed(false); }} />;
}

function LoginCard({ token, setToken, onSubmit, loading }) {
  return (
    <div className="min-h-screen bg-sand flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full shadow-xl border border-gray-100">
        <Link to="/" className="text-xs text-navy/50 hover:text-teal-600">← Back to site</Link>
        <div className="flex items-center gap-3 mt-4">
          <img src={COMPANY.logoUrl} alt="" className="w-10 h-10 rounded-full bg-navy" />
          <div className="font-heading font-semibold text-navy">Admin Dashboard</div>
        </div>
        <h1 className="font-heading text-2xl font-semibold text-navy mt-6">Sign in</h1>
        <p className="text-navy/60 text-sm mt-2">Enter the admin token to access booking management.</p>
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
          className="mt-7 space-y-4"
        >
          <div>
            <Label className="text-navy font-semibold text-sm">Admin Token</Label>
            <Input
              data-testid="admin-token-input"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="••••••••••••"
              className="mt-2"
            />
          </div>
          <button type="submit" data-testid="admin-login-submit" disabled={loading || !token} className="btn-primary w-full disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [monitorApps, setMonitorApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, a, m, s] = await Promise.all([
        listBookings(token),
        listContacts(token),
        listDriverApplications(token),
        listMonitorApplications(token),
        getStats(token),
      ]);
      setBookings(b);
      setContacts(c);
      setApplications(a);
      setMonitorApps(m);
      setStats(s);
    } catch {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await updateBookingStatus(id, status, token);
      toast.success(`Booking marked as ${status}.`);
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await deleteBooking(id, token);
      toast.success("Booking deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const changeAppStatus = async (id, status) => {
    try {
      await updateDriverApplicationStatus(id, status, token);
      toast.success(`Application marked as ${status}.`);
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const removeApp = async (id) => {
    if (!window.confirm("Delete this driver application?")) return;
    try {
      await deleteDriverApplication(id, token);
      toast.success("Application deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const changeMonitorStatus = async (id, status) => {
    try {
      await updateMonitorApplicationStatus(id, status, token);
      toast.success(`Application marked as ${status}.`);
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const removeMonitor = async (id) => {
    if (!window.confirm("Delete this monitor application?")) return;
    try {
      await deleteMonitorApplication(id, token);
      toast.success("Application deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="min-h-screen bg-sand">
      <header className="bg-white border-b border-gray-100">
        <div className="container-pad mx-auto max-w-7xl py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={COMPANY.logoUrl} alt="" className="w-10 h-10 rounded-full bg-navy object-cover" />
            <div>
              <div className="font-heading font-semibold text-navy">Comfort Care Admin</div>
              <div className="text-xs text-teal-600 uppercase tracking-widest">Bookings Dashboard</div>
            </div>
          </Link>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh" className="btn-outline !py-2 !px-4 text-sm">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={onLogout} data-testid="admin-logout" className="btn-secondary !py-2 !px-4 text-sm">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-pad mx-auto max-w-7xl py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-4 mb-10">
          <StatCard label="Total Bookings" value={stats?.total_bookings ?? "—"} icon={Calendar} color="teal" />
          <StatCard label="Pending" value={stats?.pending ?? "—"} icon={Loader2} color="amber" />
          <StatCard label="Confirmed" value={stats?.confirmed ?? "—"} icon={Users} color="blue" />
          <StatCard label="Completed" value={stats?.completed ?? "—"} icon={Users} color="emerald" />
          <StatCard label="Contact Requests" value={stats?.total_contacts ?? "—"} icon={Inbox} color="rose" />
          <StatCard label="Driver Apps" value={stats?.total_applications ?? "—"} icon={Briefcase} color="indigo" subtitle={stats?.new_applications ? `${stats.new_applications} new` : null} />
          <StatCard label="Monitor Apps" value={stats?.total_monitor_apps ?? "—"} icon={HeartHandshake} color="teal" subtitle={stats?.new_monitor_apps ? `${stats.new_monitor_apps} new` : null} />
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="bg-white">
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contact Requests ({contacts.length})</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Driver Apps ({applications.length})</TabsTrigger>
            <TabsTrigger value="monitors" data-testid="tab-monitors">Monitor Apps ({monitorApps.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6 space-y-3">
            {loading && <div className="text-center py-10 text-navy/50">Loading...</div>}
            {!loading && bookings.length === 0 && (
              <div className="bg-white rounded-2xl p-10 text-center text-navy/60">No bookings yet.</div>
            )}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-heading text-lg font-semibold text-navy">{b.full_name}</h3>
                      <StatusBadge status={b.status} />
                      <Badge variant="outline" className="capitalize text-xs">{b.trip_type.replace("_", " ")}</Badge>
                      <Badge variant="outline" className="capitalize text-xs">{b.transportation_type}</Badge>
                      {b.recurring && <Badge className="bg-teal-500 text-xs">
                        {b.recurring_days?.length
                          ? `Every ${b.recurring_days.map((d) => d.slice(0,3).replace(/^./, c => c.toUpperCase())).join("/")}${b.recurring_end_date ? ` → ${b.recurring_end_date}` : ""}`
                          : "Recurring"}
                      </Badge>}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm text-navy/70">
                      <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-teal-500" /><a href={`tel:${b.phone}`} className="hover:text-teal-600">{b.phone}</a></div>
                      <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-teal-500" /><a href={`mailto:${b.email}`} className="hover:text-teal-600 truncate">{b.email}</a></div>
                      <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-teal-500" />{b.pickup_date} at {b.pickup_time}</div>
                      <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-teal-500" /><span className="truncate">{b.pickup_address}</span></div>
                      <div className="flex items-center gap-2 sm:col-span-2"><MapPin className="w-3.5 h-3.5 text-teal-500" />→ <span className="truncate">{b.destination_address}</span></div>
                    </div>
                    {b.notes && <div className="mt-3 text-sm text-navy/60 bg-sand rounded-lg p-3"><strong>Notes:</strong> {b.notes}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                      <SelectTrigger className="w-36" data-testid={`booking-status-${b.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <button onClick={() => removeBooking(b.id)} data-testid={`booking-delete-${b.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500" aria-label="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="contacts" className="mt-6 space-y-3">
            {!loading && contacts.length === 0 && (
              <div className="bg-white rounded-2xl p-10 text-center text-navy/60">No contact requests yet.</div>
            )}
            {contacts.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-heading font-semibold text-navy">{c.name}</h3>
                  {c.consultation && <Badge className="bg-teal-500 text-xs">Consultation Request</Badge>}
                  <span className="text-xs text-navy/50 ml-auto">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-navy/70 mt-2">
                  <a href={`mailto:${c.email}`} className="hover:text-teal-600">{c.email}</a>
                  {c.phone && <a href={`tel:${c.phone}`} className="hover:text-teal-600">{c.phone}</a>}
                </div>
                <p className="text-navy/80 mt-3 bg-sand rounded-lg p-3">{c.message}</p>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="applications" className="mt-6 space-y-3">
            {!loading && applications.length === 0 && (
              <div className="bg-white rounded-2xl p-10 text-center text-navy/60">No driver applications yet.</div>
            )}
            {applications.map((a) => (
              <ApplicationRow key={a.id} app={a} onStatus={changeAppStatus} onDelete={removeApp} />
            ))}
          </TabsContent>

          <TabsContent value="monitors" className="mt-6 space-y-3">
            {!loading && monitorApps.length === 0 && (
              <div className="bg-white rounded-2xl p-10 text-center text-navy/60">No monitor applications yet.</div>
            )}
            {monitorApps.map((a) => (
              <MonitorRow key={a.id} app={a} onStatus={changeMonitorStatus} onDelete={removeMonitor} />
            ))}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, subtitle }) {
  const colorMap = {
    teal: "bg-teal-500/10 text-teal-600",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
    emerald: "bg-emerald-100 text-emerald-700",
    rose: "bg-rose-100 text-rose-700",
    indigo: "bg-indigo-100 text-indigo-700",
  };
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorMap[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-heading text-3xl font-bold text-navy mt-4">{value}</div>
      <div className="text-xs text-navy/55 uppercase tracking-wider mt-1">{label}</div>
      {subtitle && <div className="text-[10px] text-teal-600 font-bold mt-1">{subtitle}</div>}
    </div>
  );
}

function ApplicationRow({ app, onStatus, onDelete }) {
  const [open, setOpen] = useState(false);
  const positionLabel = { full_time: "Full-Time", part_time: "Part-Time", prn: "PRN" }[app.position_type] || app.position_type;
  const yn = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

  return (
    <div data-testid={`app-row-${app.id}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-heading text-lg font-semibold text-navy">{app.full_name}</h3>
              <AppStatusBadge status={app.status} />
              <Badge variant="outline" className="text-xs">{positionLabel}</Badge>
              <Badge variant="outline" className="text-xs">{app.years_driving} yrs driving</Badge>
              {app.resume_attached && <Badge className="bg-teal-500 text-xs">Resume in email</Badge>}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm text-navy/70">
              <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-teal-500" /><a href={`tel:${app.phone}`} className="hover:text-teal-600">{app.phone}</a></div>
              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-teal-500" /><a href={`mailto:${app.email}`} className="hover:text-teal-600 truncate">{app.email}</a></div>
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-teal-500" />{app.city_state}</div>
              <div className="flex items-center gap-2 text-xs"><Calendar className="w-3.5 h-3.5 text-teal-500" />{new Date(app.created_at).toLocaleDateString()}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={app.status} onValueChange={(v) => onStatus(app.id, v)}>
              <SelectTrigger className="w-40" data-testid={`app-status-${app.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="reviewing">Reviewing</SelectItem>
                <SelectItem value="interviewing">Interviewing</SelectItem>
                <SelectItem value="hired">Hired</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={() => setOpen((o) => !o)} className="p-2 rounded-lg hover:bg-sand text-navy/70" aria-label="Expand">
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <button onClick={() => onDelete(app.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" aria-label="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="bg-sand border-t border-gray-100 p-6 grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Detail label="Home Address" value={app.home_address} />
          <Detail label="Age 21+" value={yn(app.at_least_21)} />
          <Detail label="License" value={`${app.license_number} (${app.license_state})`} />
          <Detail label="Moving Violations (3yr)" value={`${yn(app.moving_violations_3yrs)}${app.moving_violations_details ? ` — ${app.moving_violations_details}` : ""}`} />
          <Detail label="DUI/DWI" value={`${yn(app.dui_dwi)}${app.dui_dwi_details ? ` — ${app.dui_dwi_details}` : ""}`} />
          <Detail label="Currently Employed" value={`${yn(app.currently_employed)}${app.current_employer ? ` — ${app.current_employer}` : ""}`} />
          <Detail label="Transport Experience" value={yn(app.transport_experience)} />
          <Detail label="Senior/Disability Experience" value={yn(app.senior_disability_experience)} />
          <Detail label="Certifications" value={app.has_certifications ? (app.certifications_list || "Yes") : "No"} />
          <Detail label="Days Available" value={app.days_available} />
          <Detail label="Hours Available" value={app.hours_available} />
          <Detail label="Background Check OK" value={yn(app.willing_background_check)} />
          <Detail label="MVR Review OK" value={yn(app.willing_mvr_check)} />
          <Detail label="Drug Screen OK" value={yn(app.willing_drug_screen)} />
          <Detail label="Can Assist Passengers" value={yn(app.can_assist_passengers)} />
          <Detail label="Can Operate Wheelchairs" value={yn(app.can_operate_wheelchairs)} />
          <Detail label="Can Lift Equipment" value={yn(app.can_lift_equipment)} />
          <div className="md:col-span-2">
            <Detail label="Relevant Experience" value={app.relevant_experience || "—"} />
          </div>
          <div className="md:col-span-2">
            <Detail label="Why Comfort Care?" value={app.interest_reason} />
          </div>
          {app.additional_comments && (
            <div className="md:col-span-2">
              <Detail label="Additional Comments" value={app.additional_comments} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold">{label}</div>
      <div className="text-navy mt-0.5">{value || "—"}</div>
    </div>
  );
}

function AppStatusBadge({ status }) {
  const m = {
    new: "bg-amber-100 text-amber-700 border-amber-200",
    reviewing: "bg-blue-100 text-blue-700 border-blue-200",
    interviewing: "bg-indigo-100 text-indigo-700 border-indigo-200",
    hired: "bg-emerald-100 text-emerald-700 border-emerald-200",
    rejected: "bg-red-100 text-red-700 border-red-200",
  };
  return (
    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border capitalize ${m[status] || "bg-gray-100"}`}>{status}</span>
  );
}

function StatusBadge({ status }) {
  const m = {
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    confirmed: "bg-blue-100 text-blue-700 border-blue-200",
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
  };
  return (
    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border capitalize ${m[status] || "bg-gray-100"}`}>{status}</span>
  );
}

const EXP_LABELS = {
  children_students: "Children/Students",
  seniors: "Seniors",
  disabilities: "Disabilities",
  healthcare: "Healthcare/Caregiving",
  transportation: "Transportation",
  customer_service: "Customer Service",
  none: "None",
};

const START_LABELS = {
  immediately: "Immediately",
  within_1_week: "Within 1 week",
  within_2_weeks: "Within 2 weeks",
  more_than_2_weeks: "More than 2 weeks",
};

const CPR_LABELS = {
  certified: "Currently certified",
  willing_to_certify: "Willing to certify",
};

function MonitorRow({ app, onStatus, onDelete }) {
  const [open, setOpen] = useState(false);
  const yn = (v) => (v === true ? "Yes" : v === false ? "No" : "—");
  const expList = (app.experience_with || []).map((v) => EXP_LABELS[v] || v).join(", ") || "—";

  return (
    <div data-testid={`monitor-row-${app.id}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-heading text-lg font-semibold text-navy">{app.full_name}</h3>
              <AppStatusBadge status={app.status} />
              <Badge variant="outline" className="text-xs">Monitor / Attendant</Badge>
              <Badge variant="outline" className="text-xs">{START_LABELS[app.start_availability] || app.start_availability}</Badge>
              {app.resume_attached && <Badge className="bg-teal-500 text-xs">Resume in email</Badge>}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm text-navy/70">
              <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-teal-500" /><a href={`tel:${app.phone}`} className="hover:text-teal-600">{app.phone}</a></div>
              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-teal-500" /><a href={`mailto:${app.email}`} className="hover:text-teal-600 truncate">{app.email}</a></div>
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-teal-500" />{app.city_state}</div>
              <div className="flex items-center gap-2 text-xs"><Calendar className="w-3.5 h-3.5 text-teal-500" />{new Date(app.created_at).toLocaleDateString()}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={app.status} onValueChange={(v) => onStatus(app.id, v)}>
              <SelectTrigger className="w-40" data-testid={`monitor-status-${app.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="reviewing">Reviewing</SelectItem>
                <SelectItem value="interviewing">Interviewing</SelectItem>
                <SelectItem value="hired">Hired</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={() => setOpen((o) => !o)} className="p-2 rounded-lg hover:bg-sand text-navy/70" aria-label="Expand">
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <button onClick={() => onDelete(app.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" aria-label="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="bg-sand border-t border-gray-100 p-6 grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Detail label="Days Available" value={app.days_available} />
          <Detail label="Hours Available" value={app.hours_available} />
          <Detail label="Morning Shifts" value={yn(app.morning_shifts)} />
          <Detail label="Afternoon Shifts" value={yn(app.afternoon_shifts)} />
          <Detail label="Start Availability" value={START_LABELS[app.start_availability] || app.start_availability} />
          <Detail label="Recent Employer" value={app.recent_employer} />
          <Detail label="Job Title" value={app.job_title} />
          <Detail label="Employment Dates" value={`${app.employment_from || "—"} → ${app.employment_to || "—"}`} />
          <div className="md:col-span-2"><Detail label="Responsibilities" value={app.job_responsibilities} /></div>
          <div className="md:col-span-2"><Detail label="Experience With" value={expList} /></div>
          {app.additional_experience && (
            <div className="md:col-span-2"><Detail label="Additional Experience" value={app.additional_experience} /></div>
          )}
          <Detail label="CPR / First Aid" value={CPR_LABELS[app.cpr_first_aid] || app.cpr_first_aid} />
          <Detail label="Willing to Train" value={yn(app.willing_training)} />
          <Detail label="Background Check OK" value={yn(app.willing_background_check)} />
          <Detail label="Drug Screen OK" value={yn(app.willing_drug_screen)} />
          <Detail label="Reliable Transportation" value={yn(app.reliable_transportation)} />
          <Detail label="Comfortable Assisting" value={yn(app.comfortable_assisting)} />
        </div>
      )}
    </div>
  );
}
